import React, {useCallback} from 'react';
import styled from 'styled-components';
import {useFaceDraft} from 'components/customer/Product/Drawer/FaceDraftContext';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';

type Tier = 'large' | 'medium' | 'small';

interface FaceHeightInputProps {
    runnerIndex: number;
    value: number;
    pixels: number;
    disabled?: boolean;
    onSet: (index: number, height: number) => void;
}

const faceTier = (pixels: number): Tier =>
    pixels > 54 ? 'large' : pixels > 26 ? 'medium' : 'small';

const sizeOf = (tier: Tier, compact = false) => {
    if (compact) {
        // the runner code shares a face barely 150px wide
        return tier === 'large'
            ? {height: 28, font: 14, width: 52, padding: '2px 5px'}
            : tier === 'medium'
              ? {height: 22, font: 13, width: 48, padding: '0 4px'}
              : {height: 18, font: 11, width: 44, padding: '0 4px'};
    }

    return tier === 'large'
        ? {height: 34, font: 22, width: 92, padding: '4px 7px'}
        : tier === 'medium'
          ? {height: 24, font: 15, width: 72, padding: '0 6px'}
          : {height: 18, font: 12, width: 72, padding: '0 5px'};
};

// Writes the same field as the panel on every keystroke, so the two never
// disagree; emptying it writes 0, as the panel does.
export const FaceHeightInput = ({
    runnerIndex,
    value,
    pixels,
    disabled = false,
    onSet,
}: FaceHeightInputProps) => {
    const compact = useCompactLayout();
    const tier = faceTier(pixels);
    const {textFor, setDraft, clearDraft, accept, isHeld, save} =
        useFaceDraft();
    const draft = textFor(runnerIndex);

    const typeHandler = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setDraft(runnerIndex, event.currentTarget.value);
        },
        [runnerIndex, setDraft]
    );

    const commitHandler = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            const text = event.currentTarget.value;
            const height = parseFloat(text.trim());
            const next = height > 0 ? height : 0;

            clearDraft();

            // tabbing through is not an edit, and would take a face off auto
            if (accept(runnerIndex, next, text, value) && next !== value) {
                onSet(runnerIndex, next);
            }
        },
        [accept, clearDraft, onSet, runnerIndex, value]
    );

    // Enter applies the height, then saves the product as other fields do,
    // unless the height does not fit
    const keyHandler = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            event.currentTarget.blur();

            if (!isHeld(runnerIndex)) save();
        },
        [isHeld, runnerIndex, save]
    );

    return (
        <Field data-cy={`drawer-height-${runnerIndex}`}>
            <Input
                $compact={compact}
                $tier={tier}
                type="text"
                inputMode="decimal"
                disabled={disabled}
                title={
                    disabled
                        ? 'One drawer has to stay on auto to take up the leftover space'
                        : undefined
                }
                aria-label={`Drawer ${runnerIndex + 1} face height in mm`}
                value={draft === null ? String(value) : draft}
                onChange={typeHandler}
                onBlur={commitHandler}
                onKeyDown={keyHandler}
            />
            <Unit $compact={compact} $tier={tier}>
                mm
            </Unit>
        </Field>
    );
};

const Field = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
`;

const Input = styled.input<{$compact: boolean; $tier: Tier}>`
    width: ${({$tier, $compact}) => sizeOf($tier, $compact).width}px;
    height: ${({$tier, $compact}) => sizeOf($tier, $compact).height}px;
    font-size: ${({$tier, $compact}) => sizeOf($tier, $compact).font}px;
    padding: ${({$tier, $compact}) => sizeOf($tier, $compact).padding};
    box-sizing: border-box;
    font-family: inherit;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.4px;
    text-align: right;
    border-radius: 3px;
    outline: none;
    cursor: text;
    color: ${({theme}) => theme.colors.primary?.main};
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(32, 67, 128, 0.16);

    &:focus {
        background: #fff;
        border-color: ${({theme}) => theme.colors.secondary?.main};
    }

    &:disabled {
        background: transparent;
        border-color: transparent;
        color: #6b7480;
        cursor: not-allowed;
    }
`;

const Unit = styled.span<{$compact: boolean; $tier: Tier}>`
    font-size: ${({$compact}) => ($compact ? 10 : 11)}px;
    color: #6b7480;
    ${({$tier}) => ($tier === 'small' ? 'display: none;' : '')}
`;
